/**
 * CIEndDecisionDialog — end-of-Clinical-Interview decision for the
 * one-clinician SLaM / 3 Borough workflow (wf.allowEndAssessmentAfterCI).
 *
 * In this workflow a single Lead clinician runs the Clinical Interview and
 * then the Semi-Structured Interview in the same appointment. Finishing the
 * CI (Risk Assessment is the last CI page because the Formulation is deferred
 * to the SSI) therefore asks a question the other workflows do not:
 *
 *   Option 1 — Proceed to Semi-Structured Interview (preselected; the normal
 *              path). The parent completes the CI exactly as the plain CI
 *              completion dialog does and navigates to the SSI.
 *   Option 2 — End assessment here. The clinician must record a reason on a
 *              second step and may also record recommended next steps; the
 *              parent then completes the CI, generates the CI report
 *              (including the "Assessment Conclusion" paragraph drafted from
 *              the reason and next steps) and locks SSI / DD / PAD until the
 *              assessment is resumed from the Patient Overview.
 *
 * Step machine (same pattern as InterviewFinishPathDialog) inside
 * ModuleCompletionDialog:
 *   1. choose  — two SelectableOptionCards + a single primary CTA whose label
 *                mirrors the selected option.
 *   2. reason  — two labelled textareas (GOV.UK question pattern):
 *                  - "Reason for ending the assessment" — required. A red
 *                    asterisk (the same mark used on other assessment forms)
 *                    sits beside the label, and the forward button stays
 *                    grey until a reason is typed. An inline error remains
 *                    as a backstop if a blank value still reaches the handler;
 *                  - "Recommended next steps" — optional. Deliberately
 *                    optional: when nothing was agreed with the patient we
 *                    want the clinician to leave it blank rather than type
 *                    filler, because the Assessment Conclusion prompt is
 *                    forbidden from inventing next steps and an explicit
 *                    "none recorded" is the honest input for it.
 *                Plus Back and the terminal CTA.
 *
 * Copy is kept short and neutral. The clinician is choosing a clinical
 * path, so the dialog states what each option does and nothing about
 * which is more usual. Worked examples live in InfoTooltips beside the
 * two labels (the same pattern as CI question info), so they are there
 * for anyone who wants them without steering the people who do not.
 *
 * The reason step asks for a taller card (max-h-[85vh], the height the
 * other form-style dialogs such as SSIOverrideDialog use) so both boxes
 * are visible without scrolling. The boxes themselves grow with the text
 * via the shared resizeTextarea helper; once they outgrow the card, the
 * body scrolls as normal.
 *
 * All step buttons are plain buttons (not AlertDialog.Action/Cancel) so the
 * dialog stays open on validation failure and while the parent's Firestore
 * writes are in flight; the parent closes it on success.
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import ModuleCompletionDialog, { CancelButton } from './ModuleCompletionDialog';
import SelectableOptionCard from './SelectableOptionCard';
import InfoTooltip from './InfoTooltip';
import { resizeTextarea } from '../utils/textareaResize';
import { CI_END_DECISIONS } from '../constants/workflowConfig';

// Textareas grow with the text up to this height, then scroll internally.
// Tall enough for a full paragraph; small enough that the card's own
// scroll (max-h-[85vh]) rarely needs to kick in.
const TEXTAREA_MAX_HEIGHT = 320;

// Worked examples, shown on demand. Each is one possibility, none is a
// recommendation; the two labels stay bare so the form does not lead.
export const END_REASON_EXAMPLES =
  'For example: the patient asked to stop; the referral question was answered during the interview; another service is better placed to help.';
export const NEXT_STEPS_EXAMPLES =
  'For example: what was agreed with the patient, or who will follow up and when.';

const STEPS = Object.freeze({
  CHOOSE: 'choose',
  REASON: 'reason',
});

// Shared Tailwind for non-Action step buttons. Mirrors ModuleCompletionDialog
// STYLES so the row looks consistent, but these must NOT wrap
// AlertDialog.Cancel/Action or the dialog would close before validation.
const BTN = {
  cancel:
    'py-2 px-4 mb-2 font-semibold border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap',
  primary:
    'flex items-center px-4 py-2 mb-2 font-semibold rounded-md transition-all duration-300 bg-violet-900 text-white hover:bg-violet-800',
  primaryDisabled:
    'flex items-center px-4 py-2 mb-2 font-semibold rounded-md bg-gray-300 text-gray-500 cursor-not-allowed',
};

export const END_ASSESSMENT_REASON_ERROR = 'Enter the reason for ending the assessment';

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {boolean} [props.isSubmitting=false]  Disables every control while the
 *   parent's Firestore writes are in flight.
 * @param {() => void|Promise} props.onProceedToSsi  Option 1 confirmed.
 * @param {(payload: { reason: string, nextSteps: string }) => void|Promise} props.onEndAssessment
 *   Option 2 confirmed with a non-empty, trimmed reason and the trimmed
 *   recommended next steps ('' when the clinician left them blank).
 */
export default function CIEndDecisionDialog({
  open,
  onOpenChange,
  isSubmitting = false,
  onProceedToSsi,
  onEndAssessment,
}) {
  const [step, setStep] = useState(STEPS.CHOOSE);
  // Proceeding is the expected path, so it is preselected: a clinician who
  // just wants to carry on presses one button. Ending needs a deliberate
  // second click plus a written reason.
  const [decision, setDecision] = useState(CI_END_DECISIONS.PROCEED_TO_SSI);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [nextSteps, setNextSteps] = useState('');
  const reasonRef = useRef(null);
  const reasonId = useId();
  const errorId = `${reasonId}-error`;
  const nextStepsId = useId();
  const nextStepsHintId = `${nextStepsId}-hint`;

  // Reset whenever the dialog opens so a previous choice or half-typed
  // reason / next steps cannot stick between visits.
  useEffect(() => {
    if (open) {
      setStep(STEPS.CHOOSE);
      setDecision(CI_END_DECISIONS.PROCEED_TO_SSI);
      setReason('');
      setReasonError('');
      setNextSteps('');
    }
  }, [open]);

  // Move focus into the reason field when the second step appears so
  // keyboard users land on the only input.
  useEffect(() => {
    if (step === STEPS.REASON) {
      reasonRef.current?.focus();
    }
  }, [step]);

  const handleClose = () => {
    if (isSubmitting) return;
    onOpenChange(false);
  };

  const handleChooseContinue = () => {
    if (isSubmitting) return;
    if (decision === CI_END_DECISIONS.END_ASSESSMENT) {
      setStep(STEPS.REASON);
      return;
    }
    onProceedToSsi();
  };

  const handleEndAssessment = () => {
    if (isSubmitting) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError(END_ASSESSMENT_REASON_ERROR);
      reasonRef.current?.focus();
      return;
    }
    setReasonError('');
    // Next steps are optional: pass '' (not undefined) so the Firestore
    // builder always writes a string and the backend reader never has to
    // distinguish "absent" from "blank".
    onEndAssessment({ reason: trimmed, nextSteps: nextSteps.trim() });
  };

  const titleByStep = {
    [STEPS.CHOOSE]: 'Clinical Interview Complete',
    [STEPS.REASON]: 'End assessment',
  };

  const descriptionByStep = {
    [STEPS.CHOOSE]: 'Choose how to continue.',
    [STEPS.REASON]:
      'These draft the Assessment Conclusion paragraph in the Clinical Interview report, which you will review.',
  };

  const chooseCtaLabel =
    decision === CI_END_DECISIONS.END_ASSESSMENT
      ? 'Continue'
      : 'Go to Semi-Structured Interview';

  const actions =
    step === STEPS.CHOOSE ? (
      <div className="w-full flex flex-wrap items-center justify-between gap-3">
        <CancelButton onClick={handleClose} />
        <button
          type="button"
          disabled={isSubmitting}
          className={isSubmitting ? BTN.primaryDisabled : BTN.primary}
          onClick={handleChooseContinue}
        >
          {isSubmitting && decision === CI_END_DECISIONS.PROCEED_TO_SSI
            ? 'Saving…'
            : chooseCtaLabel}
        </button>
      </div>
    ) : (
      <div className="w-full flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          className={BTN.cancel}
          disabled={isSubmitting}
          onClick={() => {
            setReasonError('');
            setStep(STEPS.CHOOSE);
          }}
        >
          Back
        </button>
        <button
          type="button"
          // Grey and inert until there is a real reason. Spaces do not
          // count: the handler stores the trimmed text, so the button
          // follows the same rule. Submitting keeps it grey too.
          disabled={isSubmitting || !reason.trim()}
          className={isSubmitting || !reason.trim() ? BTN.primaryDisabled : BTN.primary}
          onClick={handleEndAssessment}
        >
          {isSubmitting ? 'Ending assessment…' : 'End assessment and generate report'}
        </button>
      </div>
    );

  return (
    <ModuleCompletionDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
        else onOpenChange(next);
      }}
      title={titleByStep[step]}
      description={descriptionByStep[step]}
      width={640}
      // The reason step is a form: show both boxes without scrolling.
      maxHeightClass={step === STEPS.REASON ? 'max-h-[85vh]' : 'max-h-[60vh]'}
      actions={actions}
    >
      {step === STEPS.CHOOSE && (
        <div
          className="mb-4 space-y-3 text-left"
          role="radiogroup"
          aria-label="How to continue after Clinical Interview"
        >
          <SelectableOptionCard
            selected={decision === CI_END_DECISIONS.PROCEED_TO_SSI}
            onSelect={() => setDecision(CI_END_DECISIONS.PROCEED_TO_SSI)}
            disabled={isSubmitting}
            title="Proceed to Semi-Structured Interview"
            description="Continue this appointment with the Semi-Structured Interview."
          />
          <SelectableOptionCard
            selected={decision === CI_END_DECISIONS.END_ASSESSMENT}
            onSelect={() => setDecision(CI_END_DECISIONS.END_ASSESSMENT)}
            disabled={isSubmitting}
            title="End assessment here"
            description="Record a reason and generate the Clinical Interview report. The remaining modules are locked until the assessment is resumed."
          />
        </div>
      )}

      {step === STEPS.REASON && (
        <div className="mb-2 text-left">
          {/* Label row: bare label plus an on-demand examples tooltip. The
              tooltip is a sibling of the label, not inside it, so clicking
              it does not focus the textarea and its text is not read as
              part of the field's name. */}
          <div className="flex items-center gap-2 mb-2">
            {/* The asterisk is a sibling of the label, not inside it, so the
                field's accessible name stays "Reason for ending the assessment".
                Colour matches the required mark on the clinician assignment
                dialogs (text-red-500). The textarea's required attribute is
                what a screen reader announces. */}
            <div className="flex items-baseline">
              <label htmlFor={reasonId} className="font-semibold text-gray-900">
                Reason for ending the assessment
              </label>
              <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>
            </div>
            <InfoTooltip
              content={END_REASON_EXAMPLES}
              label="Examples of reasons for ending the assessment"
              maxWidth="320px"
            />
          </div>
          {reasonError && (
            <p id={errorId} role="alert" className="text-sm font-semibold text-red-700 mb-2">
              {reasonError}
            </p>
          )}
          <textarea
            id={reasonId}
            ref={reasonRef}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (reasonError && e.target.value.trim()) setReasonError('');
            }}
            onInput={(e) => resizeTextarea(e.target, TEXTAREA_MAX_HEIGHT)}
            rows={6}
            required
            disabled={isSubmitting}
            aria-describedby={reasonError ? errorId : undefined}
            aria-invalid={reasonError ? 'true' : 'false'}
            className={`w-full px-3 py-2 rounded-md shadow-sm text-sm resize-none focus:ring-violet-500 focus:border-violet-500 border ${
              reasonError ? 'border-red-600 border-2' : 'border-gray-300'
            }`}
          />

          <div className="flex items-center gap-2 mt-5 mb-1">
            <label htmlFor={nextStepsId} className="font-semibold text-gray-900">
              Recommended next steps (optional)
            </label>
            <InfoTooltip
              content={NEXT_STEPS_EXAMPLES}
              label="Examples of recommended next steps"
              maxWidth="320px"
            />
          </div>
          {/* The one hint kept in plain text: it tells the clinician that
              blank is a valid answer, which the generated paragraph relies on. */}
          <p id={nextStepsHintId} className="text-sm text-gray-600 mb-2">
            Leave blank if none were discussed.
          </p>
          <textarea
            id={nextStepsId}
            value={nextSteps}
            onChange={(e) => setNextSteps(e.target.value)}
            onInput={(e) => resizeTextarea(e.target, TEXTAREA_MAX_HEIGHT)}
            rows={4}
            disabled={isSubmitting}
            aria-describedby={nextStepsHintId}
            className="w-full px-3 py-2 rounded-md shadow-sm text-sm resize-none focus:ring-violet-500 focus:border-violet-500 border border-gray-300"
          />
        </div>
      )}
    </ModuleCompletionDialog>
  );
}
